import * as React from "react";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui-simple";
import { ScrollArea } from "@/components/ui-simple";
import {
  LayoutDashboard,
  Building,
  Menu,
  X,
  FileText,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

export function SupplierSidebar({ className }) {
  const [location] = useLocation();
  const [isMobileOpen, setIsMobileOpen] = React.useState(false);
  const [openSubmenus, setOpenSubmenus] = React.useState({ reports: true });
  const normalizedLocation = String(location || "")
    .split("?")[0]
    .split("#")[0];

  const isPathActive = React.useCallback(
    (href, activePaths = []) => {
      const candidates = [href, ...activePaths].filter(Boolean);
      return candidates.some(
        (path) =>
          normalizedLocation === path ||
          normalizedLocation.startsWith(`${path}/`),
      );
    },
    [normalizedLocation],
  );

  // Get user role from localStorage or auth store
  const getUserRole = () => {
    const supplierId = localStorage.getItem("SUPPLIER_ID");
    if (supplierId) return "supplier";
    return null;
  };

  const userRole = getUserRole();

  // Define menu items based on user role
  const getMenuItems = () => {
    const commonItems = [
      {
        title: "Dashboard",
        href: "/supplier-dashboard",
        icon: LayoutDashboard,
        roles: ["supplier"],
      },
      {
        title: "Registration Details",
        href: "/supplier-profile",
        icon: FileText,
        roles: ["supplier"],
      },
      {
        title: "Pricelist",
        href: "/supplier-pricelist",
        icon: FileText,
        roles: ["supplier"],
      },
    ];

    const supplierItems = [
      {
        title: "Reports",
        icon: FileText,
        roles: ["supplier"],
        submenuKey: "reports",
        children: [
          {
            title: "Purchase Order",
            href: "/reports/purchase-order",
            activePaths: ["/purchaseorderview"],
            roles: ["supplier"],
          },
          {
            title: "Purchase Report",
            href: "/reports/purchase-report",
            activePaths: ["/purchasereportview"],
            roles: ["supplier"],
          },
          {
            title: "QC Report",
            href: "/reports/qc-report",
            activePaths: ["/qcreportview"],
            roles: ["supplier"],
          },
          {
            title: "Purchase Debt Note",
            href: "/reports/purchase-debnote",
            activePaths: ["/purchasedebitnoteview"],
            roles: ["supplier"],
          },
          {
            title: "Purchase Return ",
            href: "/reports/purchase-return",
            activePaths: ["/purchasereturnview"],
            roles: ["supplier"],
          },
          {
            title: "Payment Details",
            href: "/reports/payment-details",
            activePaths: [
              "/payments",
              "/paymentdetailsview",
              "/paymantdetailsview",
            ],
            roles: ["supplier"],
          },
        ],
      },
      {
        title: "Supplier Invoice Entry",
        href: "/supplier-invoice-entry",
        icon: FileText,
        roles: ["supplier"],
      },
      {
        title: "Invoice List",
        href: "/invoice-list",
        activePaths: ["/invoicelistview"],
        icon: FileText,
        roles: ["supplier"],
      },
    ];

    // Combine items based on role
    let items = [...commonItems];

    if (userRole === "supplier") {
      items.push(...supplierItems);
    }

    return items;
  };

  const menuItems = getMenuItems();

  // Filter items based on user role
  const filteredItems = menuItems.filter((item) =>
    item.roles.includes(userRole),
  );

  const toggleSubmenu = (submenuKey) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [submenuKey]: !prev[submenuKey],
    }));
  };

  return (
    <>
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="sm"
        className="fixed z-50 shadow-md top-4 left-4 md:hidden bg-white/80 backdrop-blur-sm"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
      >
        {isMobileOpen ? <X size={20} /> : <Menu size={20} />}
      </Button>

      {/* Sidebar */}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-64 bg-gradient-to-b from-white to-gray-50/50 border-r border-gray-200/60 backdrop-blur-xl transition-transform duration-300 ease-in-out",
          isMobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0",
          className,
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center h-16 gap-3 px-6 border-b border-gray-200/60">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-blue-600 to-orange-500">
              <Building className="text-white" size={18} />
            </div>
            <div className="flex flex-col">
              <h2 className="text-sm font-semibold text-gray-900">
                Supplier Portal
              </h2>
              <p className="text-xs text-gray-500">Management System</p>
            </div>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            <nav className="space-y-2">
              {filteredItems.map((item) => {
                const Icon = item.icon;
                const hasChildren =
                  Array.isArray(item.children) && item.children.length > 0;
                const isActive = item.href
                  ? isPathActive(item.href, item.activePaths)
                  : false;
                const hasActiveChild = hasChildren
                  ? item.children.some((child) =>
                      isPathActive(child.href, child.activePaths),
                    )
                  : false;
                const isSubmenuOpen = item.submenuKey
                  ? openSubmenus[item.submenuKey]
                  : false;

                if (hasChildren) {
                  return (
                    <div key={item.title} className="space-y-1">
                      <Button
                        variant="ghost"
                        className="justify-start w-full gap-3 px-3 text-gray-800 transition-all duration-200 border border-transparent h-11 rounded-xl hover:text-gray-900 hover:bg-blue-50"
                        onClick={() => toggleSubmenu(item.submenuKey)}
                      >
                        <Icon size={18} />
                        <span
                          className={cn(
                            "flex-1 font-medium text-left",
                            hasActiveChild ? "text-blue-700" : "",
                          )}
                        >
                          {item.title}
                        </span>
                        {isSubmenuOpen ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronRight size={16} />
                        )}
                      </Button>

                      {isSubmenuOpen && (
                        <div className="pl-3 ml-6 mr-1 space-y-1 border-l border-gray-200">
                          {item.children
                            .filter((child) => child.roles.includes(userRole))
                            .map((child) => {
                              const isChildActive = isPathActive(
                                child.href,
                                child.activePaths,
                              );
                              return (
                                <Link key={child.href} href={child.href}>
                                  <Button
                                    variant="ghost"
                                    className="w-full justify-start h-9 gap-2 px-2.5 border rounded-lg transition-all duration-200 border-transparent text-gray-800 hover:text-gray-900 hover:bg-blue-50"
                                    onClick={() => setIsMobileOpen(false)}
                                  >
                                    <span
                                      aria-hidden="true"
                                      className={cn(
                                        "h-1.5 w-1.5 rounded-full bg-gray-300",
                                        isChildActive ? "bg-blue-600" : "",
                                      )}
                                    />
                                    <span
                                      className={cn(
                                        "flex-1 text-left font-medium",
                                        isChildActive ? "text-blue-700" : "",
                                      )}
                                    >
                                      {child.title}
                                    </span>
                                  </Button>
                                </Link>
                              );
                            })}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <div key={item.href} className="space-y-1">
                    <Link href={item.href}>
                      <Button
                        variant="ghost"
                        className="justify-start w-full gap-3 px-3 text-gray-800 transition-all duration-200 border border-transparent h-11 rounded-xl hover:text-gray-900 hover:bg-blue-50"
                        onClick={() => setIsMobileOpen(false)}
                      >
                        <Icon size={18} />
                        <span
                          className={cn(
                            "flex-1 font-medium text-left",
                            isActive ? "text-blue-700" : "",
                          )}
                        >
                          {item.title}
                        </span>
                        <span className="w-4" aria-hidden="true" />
                      </Button>
                    </Link>
                  </div>
                );
              })}
            </nav>
          </ScrollArea>
        </div>
      </div>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/20 backdrop-blur-sm md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
    </>
  );
}
